import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// Apple-style nav
const AppleNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/5">
    <div className="max-w-[1100px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0071e3 0%, #005ec7 100%)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
              <circle cx="9" cy="12" r="3.5" />
              <path d="M15 8 Q 19 6, 21 4" />
              <path d="M14.5 12 L 22 12" />
              <path d="M15 16 Q 19 18, 21 20" />
            </svg>
        </div>
        <span className="text-[17px] font-bold tracking-tight text-[#1d1d1f] lowercase">mindpulsei</span>
      </Link>
    </div>
  </nav>
);

const AppleInput = ({ label, id, tid, ...props }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="text-[13px] font-medium text-[#1d1d1f] tracking-tight block">{label}</label>
    <input
      id={id}
      data-testid={tid}
      className="w-full px-4 py-3 rounded-xl bg-[#f5f5f7] border border-transparent text-[15px] text-[#1d1d1f] placeholder:text-[#86868b] focus:border-current-teal focus:bg-white focus:outline-none transition-colors"
      {...props}
    />
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      const role = data.user?.role;
      if (role === 'student') navigate('/student/dashboard');
      else if (role === 'parent') navigate('/parent/dashboard');
      else if (role === 'corporate_admin') navigate('/corporate/dashboard');
      else if (role === 'employee') navigate('/corporate/employee');
      else navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match'); return;
    }
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters'); return;
    }
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';

  return (
    <div className="min-h-screen bg-white text-[#1d1d1f] antialiased">
      <AppleNav />

      <section className="relative pt-24 md:pt-28 pb-24 md:pb-36">
        <div className="max-w-[520px] mx-auto px-3 md:px-8">
          <div className="text-center pt-10 md:pt-16 mb-10 space-y-3">
            <p className="text-current-teal text-[15px] font-medium tracking-tight">mindpulsei</p>
            <h1 style={{ fontSize: 'clamp(32px, 4.5vw, 52px)', lineHeight: 1.05, letterSpacing: '-0.03em', fontWeight: 600 }}>
              {isLogin ? 'Welcome back.' : 'Create your account.'}
            </h1>
            <p className="text-[#515154] text-[16px]" style={{ lineHeight: 1.4, letterSpacing: '-0.005em' }}>
              {isLogin ? 'Continue your journey to a balanced mind.' : 'Start your journey to a balanced mind.'}
            </p>
          </div>

          {/* Segmented control */}
          <div className="rounded-full p-1 inline-flex mx-auto mb-8 bg-[#f5f5f7]" style={{ width: '100%' }}>
            <button
              type="button"
              onClick={() => setMode('login')}
              data-testid="login-tab"
              className={`flex-1 h-9 rounded-full text-[13px] font-medium tracking-tight transition-colors ${isLogin ? 'bg-white text-[#1d1d1f] shadow-sm' : 'text-[#6e6e73]'}`}
            >
              Sign in
            </button>
            <button
              type="button"
              onClick={() => setMode('register')}
              data-testid="register-tab"
              className={`flex-1 h-9 rounded-full text-[13px] font-medium tracking-tight transition-colors ${!isLogin ? 'bg-white text-[#1d1d1f] shadow-sm' : 'text-[#6e6e73]'}`}
            >
              Sign up
            </button>
          </div>

          <div className="rounded-[28px] p-8 md:p-10 bg-white border border-black/5">
            {isLogin ? (
              <form onSubmit={handleLogin} className="space-y-5">
                <AppleInput
                  label="Email"
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  required
                  tid="login-email-input"
                />
                <AppleInput
                  label="Password"
                  id="login-password"
                  type="password"
                  placeholder="Enter your password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  tid="login-password-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full mt-2 px-6 py-3 rounded-full text-[15px] font-medium bg-current-teal hover:bg-ice-mist text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Sign in
                </button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-5">
                <AppleInput
                  label="Full name"
                  id="register-name"
                  type="text"
                  placeholder="John Doe"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  required
                  tid="register-name-input"
                />
                <AppleInput
                  label="Email"
                  id="register-email"
                  type="email"
                  placeholder="you@example.com"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  required
                  tid="register-email-input"
                />
                <AppleInput
                  label="Password"
                  id="register-password"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required
                  tid="register-password-input"
                />
                <AppleInput
                  label="Confirm password"
                  id="register-confirm"
                  type="password"
                  placeholder="Re-enter password"
                  value={registerForm.confirmPassword}
                  onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                  required
                  tid="register-confirm-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full mt-2 px-6 py-3 rounded-full text-[15px] font-medium bg-current-teal hover:bg-ice-mist text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Create account
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-[13px] text-[#6e6e73] mt-6">
            {isLogin ? "Registering an organization?" : "Already have an account?"}{' '}
            {isLogin ? (
              <Link to="/corporate/register" className="text-current-teal hover:text-ice-mist font-medium">Register organization</Link>
            ) : (
              <button type="button" onClick={() => setMode('login')} className="text-current-teal hover:text-ice-mist font-medium">Sign in</button>
            )}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Login;
